import { useCallback, useEffect, useState, type MouseEvent } from 'react'
import * as Sentry from '@sentry/electron/renderer'
import { ArrowDown } from 'lucide-react'
import type { DetectedMeeting, GoogleMeetMeeting, RecallDetectedMeeting } from '@highlight/common'
import { Button } from '@highlight/ui'
import { AnimatedLabel } from '@renderer/chat'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { screenRecordingActionLabel, systemAudioActionLabel } from '@renderer/hooks/settings/audio-permission-prompt'
import { useMissingAudioPermissions } from '@renderer/hooks/settings/useMissingAudioPermissions'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { useOutOfMeetingHours } from '@renderer/overlay/Sidepanel/components/meeting/use-out-of-meeting-hours'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { calendarEventToScheduledMeeting } from '@shared/utils/connections/calendars'
import { getDetectedMeetingAppName, isRecallDetectedMeeting } from '@shared/utils/audio-notes/AudioNoteUtils'
import type { CapturePlan } from '@shared/store/impl/audioSettings'
import { useToast } from '../../use-toast'
import { useMatchingCalendarEvent } from './hooks'
import { ADD_HOURS_TO_TRANSCRIBE_CTA, useOpenHoursPaywall } from './out-of-meeting-hours'

interface NotchAudioPermissionToastProps {
  meeting?: DetectedMeeting | GoogleMeetMeeting | RecallDetectedMeeting
  plan: CapturePlan
  toastId: string
}

export function NotchAudioPermissionToast({ meeting, plan, toastId }: NotchAudioPermissionToastProps) {
  const toast = useToast()
  const meetingAppName = meeting ? getDetectedMeetingAppName(meeting) : undefined
  const { mic, systemAudio, screenRecording, isLoaded, nextPermission } = useMissingAudioPermissions({
    meetingAppName,
    plan,
  })

  useEffect(() => {
    if (isLoaded && nextPermission === null) {
      toast.dismiss(toastId)
    }
  }, [isLoaded, nextPermission, toast, toastId])

  const grant = useCallback(
    (request: () => Promise<void>) => (event: MouseEvent) => {
      event.stopPropagation()
      void request().catch((error) => {
        Sentry.captureException(error, { tags: { module: 'notch-audio-permission-toast' } })
      })
    },
    [],
  )

  let label = 'Checking permissions'
  let request: (() => Promise<void>) | null = null
  if (nextPermission === 'microphone') {
    label = 'Grant microphone access'
    request = mic.request
  } else if (nextPermission === 'systemAudio') {
    label = systemAudioActionLabel(systemAudio.actionKind, 'Grant system audio access')
    request = systemAudio.request
  } else if (nextPermission === 'screenRecording') {
    label = screenRecordingActionLabel(screenRecording.actionKind, 'Grant screen recording access')
    request = screenRecording.request
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="w-full text-center text-sm leading-5 text-white">
        Highlight needs permission to transcribe your meeting audio.
      </p>
      <Button
        variant="primary"
        tone="on-dark"
        className="h-8 w-full rounded-lg px-3 text-sm"
        disabled={!request}
        data-notch-control={`grant ${nextPermission ?? 'permission'}`}
        right={<ArrowDown aria-hidden="true" className="size-5" strokeWidth={1.5} />}
        onClick={request ? grant(request) : undefined}
      >
        <AnimatedLabel label={label} transitionMs={180} />
      </Button>
    </div>
  )
}

export function NotchTranscribingToast() {
  return <p className="w-full py-1 text-center text-sm leading-5 text-white">Your meeting is transcribing.</p>
}

interface NotchMeetingStartToastProps {
  meeting: DetectedMeeting | RecallDetectedMeeting
  toastId: string
}

export function NotchMeetingStartToast({ meeting, toastId }: NotchMeetingStartToastProps) {
  const toast = useToast()
  const { startAudioNote } = useStartAudioNote()
  const isTakingNotes = useIsTakingNotes()
  const { isIncognitoEnabled, disableIncognito } = useIncognitoMode()
  const outOfHours = useOutOfMeetingHours()
  const openHoursPaywall = useOpenHoursPaywall()
  const isRecallMeeting = isRecallDetectedMeeting(meeting)
  const recallCalendarEvent = isRecallMeeting ? meeting.calendarEvent : undefined
  const matchingCalendarEvent = useMatchingCalendarEvent(meeting)
  const calendarEvent = recallCalendarEvent ?? matchingCalendarEvent
  const [isStarting, setIsStarting] = useState(false)

  const handleStart = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation()

      if (outOfHours) {
        openHoursPaywall(calendarEvent ?? undefined)
        toast.dismiss(toastId)
        return
      }

      setIsStarting(true)
      try {
        if (isIncognitoEnabled) {
          await disableIncognito()
        }
        if (isTakingNotes) {
          await AudioNoteIpcRenderer.stopRecording(true)
        }

        const meetingToUse =
          !isRecallMeeting && calendarEvent ? calendarEventToScheduledMeeting(calendarEvent) : meeting
        await startAudioNote({ source: 'toast', meeting: meetingToUse })
        AmplitudeRenderer.trackEvent('Audio Note Action', {
          action: 'start taking notes',
          type: 'detected meeting',
          hasCalendarMatch: !!calendarEvent,
          isRecallMeeting,
        })
        toast.dismiss(toastId)
      } catch (error) {
        Sentry.captureException(error, { tags: { module: 'notch-meeting-start-toast' } })
        setIsStarting(false)
      }
    },
    [
      calendarEvent,
      disableIncognito,
      isIncognitoEnabled,
      isRecallMeeting,
      isTakingNotes,
      meeting,
      openHoursPaywall,
      outOfHours,
      startAudioNote,
      toast,
      toastId,
    ],
  )

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="w-full text-center text-sm leading-5 text-white">
        Highlight detected a meeting. Start transcribing?
      </p>
      <Button
        variant="primary"
        tone="on-dark"
        className="h-8 w-full rounded-lg px-3 text-sm"
        loading={isStarting}
        data-notch-control={outOfHours ? 'add hours' : 'start transcribing'}
        onClick={(event) => void handleStart(event)}
      >
        {outOfHours ? ADD_HOURS_TO_TRANSCRIBE_CTA : 'Start transcribing'}
      </Button>
    </div>
  )
}
